/**
 * Parses a date string, handling YYYY-MM-DD format as local date
 * @internal
 */
function parseDate(dateString: string): Date {
  // If the date string is in YYYY-MM-DD format, parse it as local date
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const [year, month, day] = dateString.split("-").map(Number);
    return new Date(year, month - 1, day);
  }

  // Check if the date string includes timezone information (Z or +/- offset)
  const hasTimezone = /Z|([+-]\d{2}(:?\d{2})?)$/.test(dateString);

  // If no timezone info, append 'Z' to treat as UTC
  const dateStringToParse = hasTimezone ? dateString : `${dateString}Z`;

  // Otherwise handle as ISO string (or potentially other formats)
  return new Date(dateStringToParse);
}

/**
 * Common date formatting options
 */
const FORMAT_OPTIONS = {
  short: {
    day: "numeric",
    month: "short",
    year: "numeric",
  },
  full: {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZoneName: "short",
  },
  monthYear: {
    month: "short",
    year: "numeric",
  },
  dayMonth: {
    day: "numeric",
    month: "short",
  },
} as const;

/**
 * Formats a date string in short format (e.g., "1 Jan 2024")
 */
export function formatDate(dateString?: string | null): string {
  if (!dateString) return "";
  return parseDate(dateString).toLocaleDateString(
    "en-US",
    FORMAT_OPTIONS.short,
  );
}
/**
 * Formats a timestamp in short format (e.g., "1 Jan 2024")
 */
export function formatTimestampShort(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString("en-US", FORMAT_OPTIONS.short);
}

/**
 * Formats a timestamp in full ISO (e.g., "1 Jan 2024")
 */
export function formatTimestampISO(timestamp: number): string {
  return new Date(timestamp).toISOString();
}

/**
 * Formats a date string with time (e.g., "Jan 1, 2024, 2:30 PM PST")
 */
export function formatFullDate(dateString: string): string {
  // Use parseDate to ensure consistent UTC handling
  return parseDate(dateString).toLocaleString("en-US", FORMAT_OPTIONS.full);
}

/**
 * Formats a timestamp as month and year (e.g., "Jan 2024")
 */
export function formatMonthYear(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString(
    "en-US",
    FORMAT_OPTIONS.monthYear,
  );
}

/**
 * Formats a timestamp as day and month (e.g., "1 Jan")
 */
export function formatDayMonth(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString(
    "en-US",
    FORMAT_OPTIONS.dayMonth,
  );
}

/**
 * Gets a timestamp for a date string, handling YYYY-MM-DD format as local
 */
export function getTimestamp(dateString: string): number {
  return parseDate(dateString).getTime();
}

/**
 * Format a date string to show time with timezone (e.g., "5:43 PM CDT")
 */
export function formatTimeWithTimezone(dateString: string): string {
  // Use parseDate to ensure consistent UTC handling
  const date = parseDate(dateString);
  return date.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZoneName: "short",
  });
}

/**
 * Formats a date as MM-DD-YYYY for filenames
 */
export function formatDateForFilename(date: Date = new Date()): string {
  return date
    .toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    })
    .replace(/\//g, "-");
}
